import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Alert, Button, Dialog} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetVehicleOfferAlternativesQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds, vehiclesRoutes} from '@dms/routes';
import {queryParams, usePermissions} from '@dms/shared';

import {composePath, useNavigate, useRequiredParams} from 'shared/web';

import {SelectVehicleDialogDataGrid} from './SelectVehicleDialogDataGrid';

export type SelectVehicleFullScreenProps = {
  onClose: () => void;
  isOpen: boolean;
  onSubmit: (invoiceId: string) => void;
};

export const BusinessCaseOffersSelectVehicleDialog = ({
  onClose,
  isOpen,
  onSubmit,
}: SelectVehicleFullScreenProps) => {
  const navigate = useNavigate();
  const {id: businessCaseId} = useRequiredParams();

  const [selectedId, setSelectedId] = useState<string>();

  // A vehicle another open Business Case offers as an alternative is refused on save; say so on
  // selection. Alternatives exist only with the catalogue, so without it nothing is looked up.
  // `currentData`, so a previous row's answer never shows for the next one.
  const vehicleOfferAlternativesQuery = useGetVehicleOfferAlternativesQuery(
    isNotNil(selectedId) && isFeatureEnabled(featureFlags.NEW_VEHICLE_CATALOGUE)
      ? {vehicleId: selectedId}
      : skipToken
  );
  const offeredElsewhere = (vehicleOfferAlternativesQuery.currentData?.items ?? []).filter(
    (item) => !item.isApplied && item.businessCaseId !== businessCaseId
  );
  const offeredElsewhereCodes = offeredElsewhere
    .map((item) => item.businessCasePublicId)
    .filter(isNotNil)
    .join(', ');

  const [canCreateBusinessCaseSelling] = usePermissions({
    permissionKeys: ['createBusinessCaseSelling'],
  });

  const handleSubmit = () => {
    if (selectedId) {
      onSubmit(selectedId);
    }
  };

  const handleCreateSaleVehicle = () => {
    onClose();
    navigate(
      composePath(vehiclesRoutes.createV2, {
        queryParams: {
          [queryParams.REDIRECT_SOURCE]: EntityResourceIds.businessCase,
          [queryParams.RETURN_URL]: composePath(businessCaseRoutes.selling, {
            params: {id: businessCaseId},
          }),
          id: businessCaseId,
        },
      })
    );
  };

  return (
    <Dialog
      data-testid={testIds.businessCase.detail('addSaleVehicle')}
      size="large"
      isOpen={isOpen}
      onClose={onClose}
      title={i18n.t('page.businessCase.labels.selectVehicle')}
      // It is a product requirement to have a button here (in the dialog header, next to
      // the close button), but we do not want to extend or update the platform Dialog
      // component because of one case. We also will not rename the existing property nor
      // add a new one because of this one case. So we use the `pagination` slot
      // as an escape hatch to satisfy the product requirement.
      pagination={
        <Button
          title={i18n.t('entity.vehicle.labels.createVehicle')}
          onClick={handleCreateSaleVehicle}
          isDisabled={!canCreateBusinessCaseSelling}
          data-testid={testIds.businessCase.detail('createSaleVehicle')}
        />
      }
      buttons={[
        {
          title: i18n.t('general.actions.cancel'),
          variant: 'secondary',
          onClick: onClose,
        },

        {
          title: i18n.t('general.labels.select'),
          variant: 'primary',
          onClick: handleSubmit,
          isDisabled:
            !selectedId || vehicleOfferAlternativesQuery.isFetching || offeredElsewhere.length > 0,
        },
      ]}
    >
      <VStack spacing={4}>
        <Show when={offeredElsewhere.length > 0}>
          <Alert
            variant="warning"
            title={i18n.t('entity.offerAlternative.source.offeredElsewhere', {
              defaultValue:
                'This vehicle is already offered in another business case. Choose another vehicle.',
            })}
            message={isNotNilOrEmpty(offeredElsewhereCodes) ? offeredElsewhereCodes : undefined}
            data-testid={testIds.businessCase.detail('addSaleVehicle-offeredElsewhere')}
          />
        </Show>
        <SelectVehicleDialogDataGrid
          data-testid={testIds.businessCase.detail('addSaleVehicle')}
          onRowSelectionChange={(rows) => {
            setSelectedId(rows[0]?.id);
          }}
        />
      </VStack>
    </Dialog>
  );
};
